import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import AppImage from '../../../components/AppImage';
import { listingsService } from '../../../services/supabaseService';
import { useLanguage } from '../../../contexts/LanguageContext';

const StepReviewPublish = ({ data, onBack, userId }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');

  const handlePublish = async () => {
    setPublishing(true);
    setError('');

    try {
      const propertyData = {
        address: data?.address || '',
        suburb: data?.suburb || '',
        state: data?.state || '',
        postcode: data?.postcode || '',
        propertyType: data?.type || 'house',
        beds: data?.beds || 0,
        baths: data?.baths || 0,
        parking: data?.parking || 0,
        region: 'australia'
      };

      const listingPayload = {
        listingType: data?.listingType || 'buy',
        price: data?.price || '0',
        descriptionShort: data?.generatedCopy?.shortDescription || '',
        descriptionLong: data?.generatedCopy?.fullDescription || '',
        saveAsDraft: false
      };

      const { data: result, error: createError } = await listingsService?.create({
        propertyData,
        listingData: listingPayload,
        agentId: userId || null
      });

      if (createError) throw createError;

      // Upload photos as media records if any
      if (data?.photos?.length > 0 && result?.listing?.id) {
        const { supabase } = await import('../../../lib/supabase');
        const mediaInserts = data?.photos?.map((photo, index) => ({
          listing_id: result?.listing?.id,
          media_type: 'photo',
          original_url: photo?.preview || '',
          order_index: index
        }));
        await supabase?.from('media')?.insert(mediaInserts);
      }

      navigate('/agent-dashboard', { state: { listingPublished: true, listingId: result?.listing?.id } });
    } catch (err) {
      console.error('Error publishing listing:', err);
      setError(err?.message || 'Failed to publish listing. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">{t('reviewPublishTitle')}</h2>
        <p className="text-muted-foreground">{t('reviewPublishDesc')}</p>
      </div>

      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-start gap-2">
          <Icon name="AlertCircle" size={16} color="var(--color-error)" className="mt-0.5" />
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        <div className="bg-muted rounded-lg p-6">
          <h3 className="font-semibold text-foreground mb-4">{t('propertyDetailsSection')}</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">{t('address')}</p>
              <p className="font-medium text-foreground">{data?.address}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t('suburb')}</p>
              <p className="font-medium text-foreground">{data?.suburb}, {data?.state} {data?.postcode}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t('type')}</p>
              <p className="font-medium text-foreground capitalize">{data?.type}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t('listingType')}</p>
              <p className="font-medium text-foreground capitalize">{data?.listingType}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t('bedrooms')}</p>
              <p className="font-medium text-foreground">{data?.beds}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t('bathrooms')}</p>
              <p className="font-medium text-foreground">{data?.baths}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t('parking')}</p>
              <p className="font-medium text-foreground">{data?.parking}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t('price')}</p>
              <p className="font-medium text-foreground">${parseFloat(data?.price || 0)?.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {data?.photos?.length > 0 && (
          <div>
            <h3 className="font-semibold text-foreground mb-3">{t('uploadPhotosTitle')} ({data?.photos?.length})</h3>
            <div className="grid grid-cols-4 gap-2">
              {data?.photos?.slice(0, 4)?.map((photo, index) => (
                <div key={photo?.id} className="aspect-square rounded-lg overflow-hidden border border-border">
                  <AppImage
                    src={photo?.preview}
                    alt={`Property photo ${index + 1} showing the property`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <h3 className="font-semibold text-foreground mb-3">{t('listingCopy')}</h3>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-muted-foreground mb-1">{t('headline')}</p>
              <p className="font-semibold text-foreground">{data?.generatedCopy?.selectedHeadline}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">{t('shortDescriptionLabel')}</p>
              <p className="text-foreground">{data?.generatedCopy?.shortDescription}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">{t('fullDescriptionLabel')}</p>
              <p className="text-foreground text-sm">{data?.generatedCopy?.fullDescription}</p>
            </div>
          </div>
        </div>

        {data?.professionalServices?.length > 0 && (
          <div>
            <h3 className="font-semibold text-foreground mb-3">{t('professionalServicesSection')}</h3>
            <div className="flex flex-wrap gap-2">
              {data?.professionalServices?.map((service) => (
                <span key={service} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm capitalize">
                  {service}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack} iconName="ArrowLeft">{t('back')}</Button>
        <Button
          type="button"
          variant="success"
          onClick={handlePublish}
          loading={publishing}
          iconName="Check"
        >
          {t('publishListing')}
        </Button>
      </div>
    </div>
  );
};

export default StepReviewPublish;